import {message} from 'antd';
import {Dispatch, SetStateAction, useCallback, useEffect, useRef} from 'react';
import {SetURLSearchParams} from 'react-router-dom';
import {Node} from 'slate';

import {saveReportData} from '../../../services/modules/ReportBuilder/ReportBuilderAPI';
import useDocumentStore from '../../../store/reportStore/documentStore';

export type SaveSource = 'manual' | 'auto' | 'comment';

const AUTO_SAVE_MS = 3 * 60 * 1000;
const SAVE_MESSAGE_KEY = 'report-save';

const SAVE_MESSAGES: Record<
  SaveSource,
  {loading: string; success: string; error: string}
> = {
  manual: {
    loading: 'Saving document…',
    success: 'Document saved',
    error: 'Failed to save document'
  },
  auto: {
    loading: 'Auto-saving document…',
    success: 'Document auto-saved',
    error: 'Auto-save failed'
  },
  comment: {
    loading: '',
    success: '',
    error: 'Failed to save document'
  }
};

interface UseReportDocumentSaveOptions {
  content: Node[];
  reportData: {_id?: string} | null | undefined;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  setReportData: Dispatch<SetStateAction<Record<string, unknown>>>;
  isLoaded: boolean;
}

interface UseReportDocumentSaveResult {
  saveDocument: (
    source?: SaveSource,
    contentOverride?: Node[]
  ) => Promise<void>;
}

export const useReportDocumentSave = ({
  content,
  reportData,
  searchParams,
  setSearchParams,
  setReportData,
  isLoaded
}: UseReportDocumentSaveOptions): UseReportDocumentSaveResult => {
  const isSavingRef = useRef(false);
  const lastSavedSnapshotRef = useRef<string | null>(null);
  const hasInitializedSnapshotRef = useRef(false);
  const setSaveStatus = useDocumentStore((s) => s.setSaveStatus);

  const getContentSnapshot = useCallback(
    () => JSON.stringify(content),
    [content]
  );

  useEffect(() => {
    if (!isLoaded || hasInitializedSnapshotRef.current) return;
    lastSavedSnapshotRef.current = getContentSnapshot();
    hasInitializedSnapshotRef.current = true;
    setSaveStatus('saved');
  }, [isLoaded, getContentSnapshot, setSaveStatus]);

  useEffect(() => {
    if (!isLoaded || !hasInitializedSnapshotRef.current) return;
    if (isSavingRef.current) return;

    const snapshot = getContentSnapshot();
    const isDirty = snapshot !== lastSavedSnapshotRef.current;
    setSaveStatus(isDirty ? 'modified' : 'saved');
  }, [content, isLoaded, getContentSnapshot, setSaveStatus]);

  const saveDocument = useCallback(
    async (
      source: SaveSource = 'manual',
      contentOverride?: Node[]
    ): Promise<void> => {
      if (isSavingRef.current) return;

      const contentToSave = contentOverride ?? content;
      const snapshot = JSON.stringify(contentToSave);
      if (source === 'auto' && snapshot === lastSavedSnapshotRef.current) {
        return;
      }

      isSavingRef.current = true;
      const messages = SAVE_MESSAGES[source];
      if (messages.loading) {
        void message.loading({
          content: messages.loading,
          key: SAVE_MESSAGE_KEY
        });
      }

      try {
        setSaveStatus('saving');

        const currentId = searchParams.get('id') ?? reportData?._id ?? '';
        const response = await saveReportData(currentId, contentToSave);

        if (!response) {
          throw new Error('Save request failed');
        }

        const savedReport = response?.data ?? null;
        const savedId = savedReport?._id ?? currentId;

        if (savedId) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('id', savedId);
          setSearchParams(nextParams);
        }

        if (savedReport) {
          setReportData(savedReport);
        }

        lastSavedSnapshotRef.current = snapshot;
        setSaveStatus('saved');
        if (messages.success) {
          void message.success({
            content: messages.success,
            key: SAVE_MESSAGE_KEY
          });
        }
      } catch (error) {
        console.error('Failed to save report details:', error);
        setSaveStatus('error');
        void message.error({content: messages.error, key: SAVE_MESSAGE_KEY});
      } finally {
        isSavingRef.current = false;
        const currentSnapshot = getContentSnapshot();
        if (currentSnapshot !== lastSavedSnapshotRef.current) {
          setSaveStatus('modified');
        }
      }
    },
    [
      content,
      getContentSnapshot,
      reportData?._id,
      searchParams,
      setReportData,
      setSaveStatus,
      setSearchParams
    ]
  );

  useEffect(() => {
    if (!isLoaded) return;

    const intervalId = setInterval(() => {
      void saveDocument('auto');
    }, AUTO_SAVE_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [isLoaded, saveDocument]);

  return {
    saveDocument
  };
};
